<template>
  <div v-if="_customer" class="text-left">
    <o-form ref="form" v-click-outside="handleClickOutside">
      <customer-duplicate
        :first-name="_customer.firstName"
        :last-name="_customer.lastName"
        :email="_customer.email"
        class="mb-4"
      />

      <div class="flex space-x-4 mb-4">
        <o-field-group
          v-slot="{ status }"
          class="w-full md:w-1/2"
          :label="$t('general.firstName')"
          :name="$t('general.firstName')"
          rules="required"
          :validation-value="_customer.firstName"
        >
          <text-field v-model="_customer.firstName" :label="$t('general.firstName')" :status="status" />
        </o-field-group>
        <o-field-group
          v-slot="{ status }"
          class="w-full md:w-1/2"
          :label="$t('general.lastName')"
          :name="$t('general.lastName')"
          rules="required"
          :validation-value="_customer.lastName"
        >
          <text-field v-model="_customer.lastName" :label="$t('general.lastName')" :status="status" />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group v-slot="{ status }" class="w-full md:w-1/2" :label="$t('general.email')" rules="email" :name="$t('general.email')">
          <text-field v-model="_customer.email" type="email" icon="MailIcon" :status="status" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.mobileCell')">
          <text-field v-model="_customer.phone" icon="SmartphoneIcon" />
        </o-field-group>
      </div>

      <div class="mb-4">
        <o-field-group v-if="customerPrivacyConsentInfo.enabled" class="w-full" :label="customerPrivacyConsentInfo.title" :rules="{ required: { allowFalse: false } }">
          <checkbox-field
            v-model="_privacyConsentAccepted"
            :label="customerPrivacyConsentInfo.label"
            :info="customerPrivacyConsentInfo.content"
          />
        </o-field-group>
      </div>

      <div class="mb-4">
        <o-field-group v-if="customerMarketingConsentInfo.enabled" class="w-full" :label="customerMarketingConsentInfo.title">
          <checkbox-field v-model="_marketingConsentAccepted" :label="customerMarketingConsentInfo.label" :info="customerMarketingConsentInfo.content" />
        </o-field-group>
      </div>

      <o-collapse flip :show-toggle="false" :clickable="false">
        <template #header="{ toggle, visible }">
          <div class="flex justify-center">
            <o-button flat outline size="sm" @click="toggle">
              {{ visible ? $t('general.showLess') : $t('general.showMore') }}
            </o-button>
          </div>
        </template>

        <div>
          <div class="flex space-x-4 mb-4">
            <o-field-group class="w-full md:w-1/2" :label="$t('general.homePhone')">
              <text-field v-model="_customer.homePhone" icon="PhoneIcon" />
            </o-field-group>
            <o-field-group class="w-full md:w-1/2" :label="$t('general.dateOfBirth')" date>
              <date-field
                v-model="_dateOfBirth"
                :label="$t('general.dateOfBirth')"
                :augmenter="Augmenters.YMD"
              />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-4">
            <o-field-group class="w-full md:w-1/2" :label="$t('customer.sendNotifications')">
              <select-field
                v-model="_sendNotifications"
                :options="CustomerNotificationOptions"
              />
            </o-field-group>
            <o-field-group class="w-full md:w-1/2" :label="$t('general.gender')">
              <select-field
                v-model="_customer.gender"
                :options="CustomerGenderOptions"
                :label="$t('general.gender')"
              />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-4">
            <o-field-group class="w-full" :label="$t('general.addressLine1')">
              <text-field v-model="_customer.address" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-4">
            <o-field-group class="w-full" :label="$t('general.addressLine2')">
              <text-field v-model="_customer.address2" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-4">
            <o-field-group class="w-full md:w-1/2" :label="$t('general.suburb')">
              <text-field v-model="_customer.suburb" />
            </o-field-group>
            <o-field-group class="w-full md:w-1/2" :label="$t('general.city')">
              <text-field v-model="_customer.city" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-4">
            <o-field-group class="w-full md:w-1/2" :label="$t('general.region')">
              <text-field v-model="_customer.region" />
            </o-field-group>
            <o-field-group class="w-full md:w-1/2" :label="$t('general.postcode')">
              <text-field v-model="_customer.postcode" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-4">
            <o-field-group class="w-full md:w-1/2" :label="$t('general.country')">
              <text-field v-model="_customer.country" />
            </o-field-group>
          </div>

          <o-custom-fields :data="_customer.customFields">
            <template #default="{fields}">
              <div
                v-for="field in fields"
                :key="field.props.id"
                class="flex space-x-4 mb-4"
              >
                <o-field-group class="w-full" :label="field.label">
                  <component
                    :is="field.component"
                    v-bind="field.props"
                    v-on="field.events"
                  />
                </o-field-group>
              </div>
            </template>
          </o-custom-fields>
        </div>
      </o-collapse>
    </o-form>
  </div>
</template>

<script setup lang="ts">
import type { DirectiveBinding } from 'vue'

import CustomerDuplicate from './Duplicate.vue'

import {
  CustomerGender,
  CustomerGenderLabels,
  CustomerNotifications,
  CustomerNotificationsLabels,
  CustomerMarketingConsent,
  CustomerPrivacyConsent,
} from '~/_api/models/customer'
import { DateYMD } from '~/_api/lib/dates'

import {
  OFieldGroup,
  OAlert,
  OCustomFields,
} from '@/components/elements'

import {
  TextField,
  DateField,
  SelectField,
  CheckboxField,
} from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

const vClickOutside = {
  mounted (el: any, binding: DirectiveBinding) {
    el._clickOutsideEvent = (event: Event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event)
      }
    }
    document.body.addEventListener('click', el._clickOutsideEvent)
  },
  unmounted (el: any) {
    document.body.removeEventListener('click', el._clickOutsideEvent)
  },
}

interface Props {
  customer: Record<string, any>
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:customer', value: any): void
}>()

const { $auth, $i18n } = useNuxtApp()

const form = ref<any>()
const ready = ref<number | false>(false)

const _customer = computed({
  get () {
    return props.customer
  },
  set (value) {
    emit('update:customer', value)
  },
})

const _sendNotifications = computed({
  get () {
    return props.customer.sendNotifications
  },
  set (value) {
    _customer.value.sendNotifications = value
  },
})

const _marketingConsentAccepted = computed({
  get () {
    return props.customer.marketingConsentAccepted === CustomerMarketingConsent.accepted
  },
  set (value) {
    _customer.value.marketingConsentAccepted = value
      ? CustomerMarketingConsent.accepted
      : CustomerMarketingConsent.declined
  },
})

const _privacyConsentAccepted = computed({
  get () {
    return props.customer.consentAccepted === CustomerPrivacyConsent.accepted
  },
  set (value) {
    const customer = _customer.value
    customer.consentAccepted = value
      ? CustomerPrivacyConsent.accepted
      : CustomerPrivacyConsent.declined
    _customer.value = customer
  },
})

const _dateOfBirth = computed({
  get () {
    return _customer.value.dateOfBirth?.value ?? null
  },
  set (value) {
    _customer.value.dateOfBirth = value != null ? DateYMD.fromInteger(value) : null
  },
})

const customerPrivacyConsentInfo = computed(() => {
  const location = ($auth as any).info.location

  return {
    enabled: location.customerConsentEnabled,
    title: location.customerConsentTitle,
    label: location.customerConsentAcceptLabel,
    content: location.customerConsentContent,
  }
})

const customerMarketingConsentInfo = computed(() => {
  const location = ($auth as any).info.location

  return {
    enabled: location.customerMarketingConsentEnabled,
    title: location.customerMarketingConsentTitle,
    label: location.customerMarketingConsentAcceptLabel,
    content: location.customerMarketingConsentContent,
  }
})

const CustomerGenderOptions = computed(() => {
  return Object.values(CustomerGender)
    .map((value) => {
      return {
        value,
        text: $i18n.t(CustomerGenderLabels[value as CustomerGender]),
      }
    })
})

const CustomerNotificationOptions = computed(() => {
  return Object.values(CustomerNotifications)
    .filter((value): value is CustomerNotifications => typeof value === 'number')
    .map((value) => {
      return {
        label: $i18n.t(CustomerNotificationsLabels[value]),
        value,
      }
    })
})

onMounted(() => {
  ready.value = Date.now()
})

const handleClickOutside = (e: Event) => {
  const since = Date.now() - (ready.value as number)

  if (since > 200) {
    form.value.validate()
  }
}

const validate = () => form.value?.validate()

defineExpose({ validate })
</script>
